export interface GlobeDestination {
  name: string;
  latitude: number;
  longitude: number;
}

// Representative places for locale variants, not a claim of exclusive ownership
// of a language. Language-only and non-geographic locales have explicit choices.
const REGIONS: Record<string, GlobeDestination> = {
  US: { name: "United States", latitude: 38.9, longitude: -77 },
  GB: { name: "United Kingdom", latitude: 51.5, longitude: -0.1 },
  AU: { name: "Australia", latitude: -33.9, longitude: 151.2 },
  IN: { name: "India", latitude: 28.6, longitude: 77.2 },
  ES: { name: "Spain", latitude: 40.4, longitude: -3.7 },
  FR: { name: "France", latitude: 48.9, longitude: 2.4 },
  CA: { name: "Canada", latitude: 45.5, longitude: -73.6 },
  DE: { name: "Germany", latitude: 52.5, longitude: 13.4 },
  IT: { name: "Italy", latitude: 41.9, longitude: 12.5 },
  BR: { name: "Brazil", latitude: -22.9, longitude: -43.2 },
  PT: { name: "Portugal", latitude: 38.7, longitude: -9.1 },
  JP: { name: "Japan", latitude: 35.7, longitude: 139.7 },
  NL: { name: "Netherlands", latitude: 52.4, longitude: 4.9 },
  CN: { name: "China", latitude: 39.9, longitude: 116.4 },
  KR: { name: "South Korea", latitude: 37.6, longitude: 127 },
  EG: { name: "Egypt", latitude: 30, longitude: 31.2 },
  AE: { name: "United Arab Emirates", latitude: 24.5, longitude: 54.4 },
  LB: { name: "Lebanon", latitude: 33.9, longitude: 35.5 },
  ID: { name: "Indonesia", latitude: -6.2, longitude: 106.8 },
  TR: { name: "Türkiye", latitude: 41, longitude: 29 },
  VN: { name: "Vietnam", latitude: 21, longitude: 105.8 },
  PL: { name: "Poland", latitude: 52.2, longitude: 21 },
  RU: { name: "Russia", latitude: 55.8, longitude: 37.6 },
  TH: { name: "Thailand", latitude: 13.8, longitude: 100.5 },
  SE: { name: "Sweden", latitude: 59.3, longitude: 18.1 },
  NO: { name: "Norway", latitude: 59.9, longitude: 10.8 },
  DK: { name: "Denmark", latitude: 55.7, longitude: 12.6 },
  FI: { name: "Finland", latitude: 60.2, longitude: 24.9 },
  GR: { name: "Greece", latitude: 38, longitude: 23.7 },
  IL: { name: "Israel", latitude: 32.1, longitude: 34.8 },
  UA: { name: "Ukraine", latitude: 50.5, longitude: 30.5 },
  BG: { name: "Bulgaria", latitude: 42.7, longitude: 23.3 },
  HR: { name: "Croatia", latitude: 45.8, longitude: 16 },
  CZ: { name: "Czechia", latitude: 50.1, longitude: 14.4 },
  HU: { name: "Hungary", latitude: 47.5, longitude: 19 },
  RO: { name: "Romania", latitude: 44.4, longitude: 26.1 },
  RS: { name: "Serbia", latitude: 44.8, longitude: 20.5 },
  SK: { name: "Slovakia", latitude: 48.1, longitude: 17.1 },
  SI: { name: "Slovenia", latitude: 46.1, longitude: 14.5 },
  KE: { name: "Kenya", latitude: -1.3, longitude: 36.8 },
  ZA: { name: "South Africa", latitude: -33.9, longitude: 18.4 },
  ET: { name: "Ethiopia", latitude: 9, longitude: 38.8 },
  MY: { name: "Malaysia", latitude: 3.1, longitude: 101.7 },
  PH: { name: "Philippines", latitude: 14.6, longitude: 121 },
  PK: { name: "Pakistan", latitude: 33.7, longitude: 73.1 },
  AF: { name: "Afghanistan", latitude: 34.6, longitude: 69.2 },
  IR: { name: "Iran", latitude: 35.7, longitude: 51.4 },
  NP: { name: "Nepal", latitude: 27.7, longitude: 85.3 },
  LK: { name: "Sri Lanka", latitude: 6.9, longitude: 79.9 },
  MM: { name: "Myanmar", latitude: 16.9, longitude: 96.2 },
  KH: { name: "Cambodia", latitude: 11.6, longitude: 104.9 },
  LA: { name: "Laos", latitude: 18, longitude: 102.6 },
  EE: { name: "Estonia", latitude: 59.4, longitude: 24.8 },
  LV: { name: "Latvia", latitude: 56.9, longitude: 24.1 },
  LT: { name: "Lithuania", latitude: 54.7, longitude: 25.3 },
  IS: { name: "Iceland", latitude: 64.1, longitude: -21.9 },
  MT: { name: "Malta", latitude: 35.9, longitude: 14.5 },
  AL: { name: "Albania", latitude: 41.3, longitude: 19.8 },
  AM: { name: "Armenia", latitude: 40.2, longitude: 44.5 },
  GE: { name: "Georgia", latitude: 41.7, longitude: 44.8 },
  KZ: { name: "Kazakhstan", latitude: 43.2, longitude: 76.9 },
  MK: { name: "North Macedonia", latitude: 42, longitude: 21.4 },
  MN: { name: "Mongolia", latitude: 47.9, longitude: 106.9 },
  UZ: { name: "Uzbekistan", latitude: 41.3, longitude: 69.2 },
  TM: { name: "Turkmenistan", latitude: 37.9, longitude: 58.4 },
  HK: { name: "Hong Kong", latitude: 22.3, longitude: 114.2 },
};

const SPECIAL: Record<string, GlobeDestination> = {
  "es-US": { name: "Latin America", latitude: 19.4, longitude: -99.1 },
  "ar-XA": { name: "Arabic-speaking world", latitude: 24.7, longitude: 46.7 },
  pap: { name: "Curaçao", latitude: 12.1, longitude: -68.9 },
};

export function globeDestination(code: string | null): GlobeDestination | null {
  if (!code) return null;
  if (SPECIAL[code]) return SPECIAL[code];
  const region = code.split("-").find((part, index) => index > 0 && /^[A-Z]{2}$/.test(part));
  return region ? (REGIONS[region] ?? null) : null;
}

/** Ground moves right beneath the original, left-facing running sprite. */
export function westwardLongitude(current: number, destination: number) {
  return current - ((((current - destination) % 360) + 360) % 360);
}
